// A letter written in the block editor (decision 034, um/2026-10-30/04): its markdown goes out as HTML with a plain-text part, a
// plain letter that came in becomes markdown for the editor, and a reply quotes it in a quote block.

import { escapeLine, parseBlocks, sameList, type Block } from './blocks.ts';
import { escInline, parseInline, RECORD_HREF, type InlineNode } from './inline.ts';
import { markdownToHtml } from './render.ts';

/** Plain text as markdown that reads back as the same lines: marks and block starts are escaped, empty lines part paragraphs. */
export const plainToMarkdown = (text: string) =>
  (text ?? '').replace(/\r\n?/g, '\n').split('\n').map((l) => (l.trim() ? escapeLine(escInline(l.trimEnd())) : '')).join('\n').replace(/\n{3,}/g, '\n\n').trim();

/** Plain text in a quote block: every line, empty ones too, so the quoted letter keeps its shape. */
export const quoteMarkdown = (text: string) =>
  (text ?? '').replace(/\r\n?/g, '\n').replace(/\s+$/, '').split('\n').map((l) => (l.trim() ? `> ${escInline(l.trimEnd())}` : '>')).join('\n');

function inlinePlain(nodes: InlineNode[]): string {
  return nodes.map((n) => {
    if (n.t === 'text' || n.t === 'code') return n.v;
    const inner = inlinePlain(n.c);
    if (n.t !== 'link' || RECORD_HREF.test(n.href) || !/^(https?:|mailto:)/i.test(n.href)) return inner;
    const href = n.href.replace(/^mailto:/i, '');
    return inner === href || inner === n.href ? inner : `${inner} (${href})`;
  }).join('');
}

function blockPlain(b: Block, n: number): string {
  const t = inlinePlain(parseInline(b.text));
  switch (b.kind) {
    case 'bullet': return `- ${t}`;
    case 'number': return `${n}. ${t}`;
    case 'todo': return `[${b.checked ? 'x' : ' '}] ${t}`;
    case 'quote': return t.split('\n').map((l) => `> ${l}`).join('\n');
    case 'code': return b.text;
    case 'divider': return '----------';
    case 'file': return b.text;
    default: return t;
  }
}

/** The plain-text part of a letter: what the reader sees, without the marks' characters; links keep their address in brackets. */
export function markdownToText(md: string): string {
  const out: string[] = [];
  let prev: Block | null = null;
  let n = 0;
  for (const b of parseBlocks(md)) {
    if (b.kind === 'p' && !b.text.trim()) continue;
    n = b.kind === 'number' && prev?.kind === 'number' ? n + 1 : 1;
    if (prev) out.push(sameList(prev, b) ? '\n' : '\n\n');
    out.push(blockPlain(b, n));
    prev = b;
  }
  return out.join('');
}

// Mail programs drop a page's styles, so the few the blocks need are written on the tags.
const STYLE: [RegExp, string][] = [
  [/<blockquote>/g, '<blockquote style="margin:0 0 12px;padding:0 0 0 12px;border-left:3px solid #d0d7de;color:#57606a">'],
  [/<aside class="callout">/g, '<div style="margin:0 0 12px;padding:8px 12px;border-radius:6px;background:#fff8e1">'],
  [/<\/aside>/g, '</div>'],
  [/<pre>/g, '<pre style="margin:0 0 12px;padding:8px 12px;border-radius:6px;background:#f6f8fa;font-size:13px;white-space:pre-wrap">'],
  [/<p>/g, '<p style="margin:0 0 12px">'],
  [/<hr>/g, '<hr style="border:0;border-top:1px solid #d0d7de;margin:16px 0">'],
];

/** The HTML part of a letter: the blocks as the reader's HTML, without the account's own links and files (attachments go apart). */
export function letterHtml(md: string): string {
  // A bare address in a letter is a link (signed links of service letters, an address typed by hand); a link's own address is not bare.
  // Lines inside a code fence are code: an address there stays as typed (MiniMax review P2).
  let fenced = false;
  const linked = md.split('\n').map((l) => { if (/^```/.test(l)) { fenced = !fenced; return l; } return fenced ? l : l.replace(/(^|\s|(?<!\])\()(https?:\/\/[^\s<>()[\]]*[^\s<>()[\].,;:!?'"»])/g, (_, pre: string, url: string) => `${pre}[${url.replace(/[*~`]/g, '\\$&')}](${url})`); }).join('\n');
  let html = markdownToHtml(linked, { record: () => null, file: () => null });
  for (const [re, to] of STYLE) html = html.replace(re, to);
  return `<div style="font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;font-size:14px;line-height:1.55;color:#1f2328">${html}</div>`;
}
